import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppLoaderService } from '../services/app-loader.service';
import { AppGlobalService } from '../services/app.global.service';

import { GlobalConstants } from './global.constants';
import { Store } from '@ngrx/store';
import { logoutUser } from '../STORE/actions/auth.actions';

@Injectable({
  providedIn: 'root'
})
export class HttpBaseService {
  http = inject(HttpClient);
  loader = inject(AppLoaderService);
  appGlobalService = inject(AppGlobalService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private store = inject(Store);

  private extendUrl(url, options) {
    if (options.withAppIdInUrl) {
      url += `/${this.appGlobalService.get('appId')}`;
    } else if (options.withOrgIdInUrl) {
      url += `/${this.appGlobalService.get('orgId')}`;
    } else if (options.withAppIdSortInUrl) {
      url += `/${this.appGlobalService.get('appId')}/${this.appGlobalService.get('field')}/${this.appGlobalService.get('direction')}`;
    } else if (options.withOrgIdSortInUrl) {
      url += `/${this.appGlobalService.get('orgId')}/${this.appGlobalService.get('field')}/${this.appGlobalService.get('direction')}`;
    } else if (options.withSortInUrl) {
      url += `/${this.appGlobalService.get('field')}/${this.appGlobalService.get('direction')}`;
    } else if (options.withSidToBeSent) {
      url += `/${this.appGlobalService.get('sidToBeSent')}`;
    }
    return url;
  }

  private extendParams(options, params) {
    if (!params) {
      return params;
    }
    if (options.withAppIdInPayload || options.withOrgIdInPayload) {
      if (typeof params === 'string') {
        params = JSON.parse(params);
      }
      if (options.withAppIdInPayload) {
        params.appId = this.appGlobalService.get('appId');
      } else if (options.withOrgIdInPayload) {
        params.orgId = this.appGlobalService.get('orgId');
      }
      return JSON.stringify(params);
    }
    let mutableParams = params;
    if (params?.removeUrlVariables) {
      mutableParams = { ...params };
      mutableParams.removeUrlVariables.forEach((paramToRemove: string) => {
        try {
          delete mutableParams[paramToRemove];
        } catch (e) {
          console.error('Error deleting URL parameter:', e);
        }
      });
      delete mutableParams['removeUrlVariables'];
    }
    return mutableParams;
  }

  private addQueryParams(url: string, paramsMap: Record<string, any>): string {
    if (!paramsMap) {
      return url;
    }
    const queryParams = Object.keys(paramsMap)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(paramsMap[key])}`)
      .join('&');
    return `${url}?${queryParams}`;
  }

  public get(options: any, params?: any, responseType?: any): any {
    if (options.showSpinner) {
      this.loader.show();
    }
    const httpOptions = this.getHttpHeaders(options, params);
    let url = this.normalizeUrl(options, params);
    url = this.extendUrl(url, options);
    params = this.extendParams(options, params);
    if (params?.queryParams) url = this.addQueryParams(url, params.queryParams);
    if (responseType) {
      httpOptions.responseType = responseType;
    }
    return this.http.get(url, httpOptions).pipe(
      tap((_) => {
        if (options.showSpinner) {
          this.loader.hide();
        }
        // Only dispatch online if the service currently thinks we are offline
        // This prevents redundant events that trigger UI refreshes or API loops
        if (this.appGlobalService.isOnline$.value === false) {
          window.dispatchEvent(new Event('online'));
        }
      }),
      catchError((err: any, caught: Observable<any>) => {
        if (options.showErrorMsg !== false) {
          return this.handleError(options, err);
        }
        // Hide spinner on error even when showErrorMsg is false
        if (options.showSpinner) {
          this.loader.hide();
        }
        return throwError(err);
      })
    );
  }

  /**
   * Generates a fully qualified URL for SSE or other direct browser calls.
   * @param options - API constant object
   * @param params - URL template parameters
   * @returns Fully qualified URL as string
   */
  public getFullUrl(options: any, params?: any): string {
    let url = this.normalizeUrl(options, params);
    url = this.extendUrl(url, options);

    // If it's a relative path, prefix with current origin for SSE compatibility
    if (url.startsWith('/')) {
      url = `${window.location.origin}${url}`;
    }

    // Convert params to query string for GET requests if they aren't used in URL template
    if (options.method?.toLowerCase() === 'get' && params && typeof params === 'object') {
      const queryParams = Object.keys(params)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
      if (queryParams) {
        url = url.includes('?') ? `${url}&${queryParams}` : `${url}?${queryParams}`;
      }
    }

    return url;
  }

  public post(options: any, params?: any, data?: any) {
    if (options.showSpinner) {
      this.loader.show();
    }

    const httpOptions = this.getHttpHeaders(options);
    let url = this.normalizeUrl(options, params);

    url = this.extendUrl(url, options);
    params = this.extendParams(options, params);
    if (params?.queryParams) {
      url = this.addQueryParams(url, params.queryParams);
      params = { ...params };
      delete params.queryParams;
    }

    return this.http.post(url, params, httpOptions).pipe(
      tap((_) => {
        if (options.showSpinner) {
          this.loader.hide();
        }
        // If a request succeeds, the app is clearly online
        window.dispatchEvent(new Event('online'));
        if (options.liteMessage) {
          //this.notification.lite(AppConstants.saved);
        } else if (options.showMessage !== false) {
          //this.notification.success(options.successMsg ? options.successMsg : AppConstants.recordUpdatedMsg);
        }
      }),
      catchError((err: any) => {
        if (options.showErrorMsg !== false) {
          return this.handleError(options, err);
        }
        // Hide spinner on error even when showErrorMsg is false
        if (options.showSpinner) {
          this.loader.hide();
        }
        return throwError(err);
      })
      // catchError(this.handleCatchError(err));
    );
  }

  public put(options: any, params?: any, data?: any) {
    if (options.showSpinner) {
      this.loader.show();
    }

    const httpOptions = this.getHttpHeaders(options);
    let url = this.normalizeUrl(options, params);

    url = this.extendUrl(url, options);
    params = this.extendParams(options, params);

    return this.http.put(url, params, httpOptions).pipe(
      tap((_) => {
        if (options.showSpinner) {
          this.loader.hide();
        }
        // If a request succeeds, the app is clearly online
        window.dispatchEvent(new Event('online'));
        if (options.liteMessage) {
          // this.notification.lite(AppConstants.saved);
        } else if (options.showMessage !== 'false') {
          //this.notification.success(options.successMsg ? options.successMsg : AppConstants.recordUpdatedMsg);
        }
      }),
      catchError((err: any, caught: Observable<any>) => {
        if (options.showErrorMsg !== false) {
          return this.handleError(options, err);
        }
        return throwError(err);
      })
    );
  }

  public delete(options: any, params?: any) {
    if (options.showSpinner) {
      this.loader.show();
    }

    const httpOptions = this.getHttpHeaders(options);
    let url: string = this.normalizeUrl(options, params);

    url = this.extendUrl(url, options);
    params = this.extendParams(options, params);
    if (params?.queryParams) {
      url = this.addQueryParams(url, params.queryParams);
      params = { ...params };
      delete params.queryParams;
    }

    if (params && typeof params === 'object' && Object.keys(params).length > 0) {
      httpOptions.body = params;
    }

    return this.http.delete(url, httpOptions).pipe(
      tap((_) => {
        if (options.showSpinner) {
          this.loader.hide();
        }
        // If a request succeeds, the app is clearly online
        window.dispatchEvent(new Event('online'));
        if (options.showMessage !== 'false') {
          //this.notification.success(options.successMsg ? options.successMsg : AppConstants.recordDeletedMsg);
        }
      }),
      catchError((err: any, caught: Observable<any>) => {
        if (options.showErrorMsg !== false) {
          return this.handleError(options, err);
        }
        return throwError(err);
      })
    );
  }

  private normalizeUrl(options: any, params?: any): string {
    const baseUrl = options.url || options;

    let buildUrlPrefix: string;

    if (options.serviceType === 'companion') {
      buildUrlPrefix = environment.endPoints.companion;
    } else if (options.serviceType === 'blueprint') {
      buildUrlPrefix = environment.endPoints.blueprint;
    } else {
      buildUrlPrefix = `/${environment.apihost}`;
    }

    if (baseUrl?.indexOf('{') === -1 || !params) {
      return `${buildUrlPrefix}/${baseUrl}`;
    }

    const splitUrl = baseUrl.split('/');
    if (splitUrl.length <= 1) {
      return '';
    }

    for (const segment of splitUrl) {
      if (!segment) continue;
      buildUrlPrefix = this.appendUrlSegment(buildUrlPrefix, segment, params);
    }

    delete params.pathParams;

    return encodeURI(buildUrlPrefix) || '';
  }

  private appendUrlSegment(buildUrl: string, segment: string, params: any): string {
    if (this.isUrlPlaceholder(segment)) {
      return this.appendPlaceholderValue(buildUrl, segment, params);
    }
    return this.appendLiteralSegment(buildUrl, segment);
  }

  private isUrlPlaceholder(segment: string): boolean {
    return segment[0] === '{' && segment[segment.length - 1] === '}';
  }

  private appendPlaceholderValue(buildUrl: string, segment: string, params: any): string {
    const param = segment.slice(1, -1);
    const urlParameter = params[param] || params?.pathParams?.[param];
    if (urlParameter !== undefined && urlParameter !== null) {
      return `${buildUrl}/${urlParameter}`;
    }
    return buildUrl;
  }

  private appendLiteralSegment(buildUrl: string, segment: string): string {
    const separator = buildUrl.endsWith('/') ? '' : '/';
    return buildUrl + separator + segment;
  }

  public getServiceUrl(url: string) {
    const serviceUrl = this.normalizeUrl({ url });
    return serviceUrl;
  }

  private getHttpHeaders(options: any, params?: any) {
    let contentTypeHeader: string = options.headers ? options.headers['Content-Type'] : '';

    if (!contentTypeHeader) {
      contentTypeHeader = GlobalConstants.defaultContentType; // AppConstants.defaultContentType;
    }

    // Check for referredAppId in params first, then in appGlobalService, then fall back to appId logic
    let appIdToUse = '';

    if (!options?.url?.includes('apps/createApp')) {
      if (params?.referredAppId) {
        appIdToUse = params.referredAppId;
      } else {
        appIdToUse = this.appGlobalService.get('appId') === 'appId'  || this.appGlobalService.get('appId') ? this.appGlobalService.get('appId') : this.appGlobalService.get('suiteId'); 
      }
    }

    const headersConfig: any = {
      'Content-Type': contentTypeHeader,
      'x-rappit-application': appIdToUse,
      'x-rappit-organization': this.appGlobalService.get('orgId')
    };

    if (options.serviceType === 'companion') {
      headersConfig['x-rappit-user'] = this.appGlobalService.get('userEmail');
      headersConfig['x-rsession'] = this.getCookie('RSESSION');
    }

    const httpOptions: any = {
      headers: new HttpHeaders(headersConfig)
    };

    if (options.serviceType === 'companion') {
      httpOptions.withCredentials = true;
    }

    const responsType: string = options.responseType ? options.responseType : '';
    if (responsType) {
      httpOptions.responseType = responsType;
    }
    return httpOptions;
  }

  private handleCatchError(err) {
    console.log(err);
  }

  private handleError(options: any, err: HttpErrorResponse) {
    if (options.showSpinner) {
      this.loader.hide();
    }

    // Traffic-based detection: Status 0 is standard network failure.
    // Status 504 (Gateway Timeout) is common in local development (Proxy/HMR) when the upstream is down.
    // Guard with navigator.onLine: status 0 also occurs for aborted/canceled requests, CORS failures,
    // and component teardown - none of which mean the internet is actually down. Only trust the signal
    // when the browser itself reports no connectivity, avoiding false "Internet down" dialogs.
    const networkErrorStatuses = [0, 504];
    if (networkErrorStatuses.includes(err.status) && !navigator.onLine) {
      console.error(`Network error detected (status ${err.status}). Dispatched offline event.`);
      window.dispatchEvent(new Event('offline'));
    }

    let errorMessage = 'Unknown error!';
    let isUnauthenticated = false;
    if (Object.keys(GlobalConstants.concurrencyErrorMap).includes(err?.error?.ERROR_CODE)) {
      return throwError(err);
    }
    if (err.error instanceof ErrorEvent) {
      /** A client-side or network error occurred. */
      console.error('An error occurred:', err.error.message);
      errorMessage = err.error.message;
    } else {
      try {
        errorMessage = this.translate.instant(err.error.MESSAGE);
        if (errorMessage === 'You are accessing inactive organization. please contact your administrator') {
          this.appGlobalService.write(GlobalConstants.tenantId, null);
          localStorage.removeItem('allUsers');
          this.store.dispatch(logoutUser());
        }
      } catch (e) {
        isUnauthenticated = true;
      }
      if (!isUnauthenticated) {
        this.showMessage('error', errorMessage);
      }
    }
    return throwError(err);
  }

  public getPrototypingData(options: any, params?: any): any {
    if (options.showSpinner !== 'false') {
      //this.loader.show();
    }

    const url = options.url;

    return this.http.get(url).pipe(
      tap((_) => {
        //this.loader.hide();
      }),
      catchError((err: any, caught: Observable<any>) => {
        return this.handleError(options, err);
      })
    );
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  private getCookie(name: string): string {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
    return '';
  }
}
